import React from "react";
import { Link } from "react-router-dom";
import { Stack, Icon } from "@fluentui/react";
import { DataGrid, GridColDef } from "@mui/x-data-grid";
import { Tooltip } from "@mui/material";
import { CatalystTabTooltips } from "../../Common/HealthView.constants";

export interface CatalystTableRow {
  id: string | number;
  customerName: string;
  crmlink: string;
  tpAccountName: string;
  segmentGroup: string;
  segment: string;
  industry: string;
  executiveBuddy: string;
  mtpCompositeScore: number;
  shiScore: number;
  mtpHealthScoreMDA: number;
  mtpHealthScoreMDC: number;
  mtpHealthScoreMDE: number;
  mtpHealthScoreMDI: number;
  mtpHealthScoreMDO: number;
  mtpHealthScoreSentinel: number;
  cxEReportedSentimentScore: number;
  cxEReportedSentimentColor: string;
  cxEReportedSentimentLabel: string;
  customerScoreCSATDate: string;
  fieldReportSentimentScore: number;
  fieldReportSentimentColor: string;
  fieldReportSentimentLabel: string;
  fieldTeamCSATDate: string;
  forecastToGreenDate: string;
  accountTeamEngaged: string;
  scopingSentimentassessment: string;
  actionPlan: string;
  scoreCardCompletion: string;
  cxECatalystPM: string;
  totalM365E5Seats: number;
  region: string;
  tpid: string;
  tenantId: string;
  mtpACRRange: string;
  aiSummaryonCompositeScore: string;
  customersKeyObjectivesSuccessMeasures: string;
  overallCatalystEngagement: string;
  supportStatus: string;
  customerSentimentDriversandInsights: string;
  protectionInsights: string;
  emergingRisk: string;
  weeklyTweet: string;
}

export interface CatalystDataTableProps {
  rows: CatalystTableRow[];
  isLoading?: boolean;
}

const getScoreCellColor = (value: number): string => {
  if (value >= 70) return "#73AA24";
  if (value >= 40) return "#D0B232";
  return "#D77440";
};

const scoreRenderCell = (params: any) => (
  <div
    style={{
      backgroundColor: getScoreCellColor(params.value),
      color: "#000000",
      fontWeight: 600,
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
      width: "100%",
      height: "100%",
    }}
  >
    {params.value}
  </div>
);

const yesNoRenderCell = (params: any) =>
  params.value?.toLowerCase() === "yes" || params.value?.toLowerCase() === "true" ? (
    <Icon iconName="CheckMark" styles={{ root: { color: "#73AA24", fontSize: 16, fontWeight: 700 } }} />
  ) : (
    <Icon iconName="Cancel" styles={{ root: { color: "#D77440", fontSize: 16, fontWeight: 700 } }} />
  );

const toBooleanSortRank = (value?: string): number => {
  const normalized = (value ?? "").trim().toLowerCase();
  if (["yes", "true", "1"].includes(normalized)) return 2;
  if (["no", "false", "0"].includes(normalized)) return 1;
  return 0;
};

const booleanLikeSortComparator = (v1: string, v2: string): number => {
  return toBooleanSortRank(v1) - toBooleanSortRank(v2);
};

const COLUMN_WIDTH_UNIT = 115;

const baseColumns: GridColDef[] = [
  {
    field: "customerName",
    headerName: "Customer Name",
    flex: 2.8,
    minWidth: 180,
    align: "left",
    headerAlign: "center",
    renderCell: (params: any) => {
      const tenantId = params.row.tenantId;
      if (!tenantId || tenantId === "-") return params.value;
      return (
        <Link
          to={`/customer?id=${encodeURIComponent(tenantId)}&tab=customerHealth&from=explorehealth`}
          className="catalyst-customer-link"
          title={`Navigate to ${params.value} customer page`}
        >
          <span>{params.value}</span>
          <svg width="10" height="10" viewBox="0 0 10 10" fill="currentColor" style={{ flexShrink: 0, opacity: 0.7 }}>
            <path d="M1 9L9 1M9 1H3M9 1V7" stroke="currentColor" strokeWidth="1.5" fill="none" strokeLinecap="round" strokeLinejoin="round" />
          </svg>
        </Link>
      );
    },
  },
  {
    field: "crmlink",
    headerName: "CRM Link",
    flex: 0.5,
    minWidth: 52,
    align: "center",
    headerAlign: "center",
    sortable: false,
    filterable: false,
    renderCell: (params: any) => {
      const url = params.value as string;
      if (!url || url === "-") return null;
      return (
        <a
          href={url}
          target="_blank"
          rel="noopener noreferrer"
          title="Open CRM link"
          aria-label="Open CRM link"
          style={{ color: "#0078D4", display: "inline-flex", alignItems: "center" }}
        >
          <Icon iconName="Link" styles={{ root: { fontSize: 16 } }} />
        </a>
      );
    }
  },
  { field: "executiveBuddy", headerName: "Exec Buddy", flex: 1.5, minWidth: 115, align: "center", headerAlign: "center" },
  { field: "mtpCompositeScore", headerName: "MTP Composite Score", flex: 0.7, minWidth: 60, align: "center", headerAlign: "center", renderCell: scoreRenderCell },
  { field: "shiScore", headerName: "Support Health Score", flex: 0.7, minWidth: 60, align: "center", headerAlign: "center", renderCell: scoreRenderCell },
  {
    field: "cxEReportedSentimentLabel",
    headerName: "Cx Reported Sentiment",
    flex: 1,
    minWidth: 60,
    align: "center",
    headerAlign: "center",
    renderCell: (params: any) => {
      const raw = (params.row.cxEReportedSentimentColor ?? "").toLowerCase().trim();
      const SENTIMENT_COLORS: Record<string, string> = { green: "#73AA24", yellow: "#D0B232", red: "#D77440" };
      const bg = SENTIMENT_COLORS[raw] ?? (raw && raw !== "-" ? raw : "transparent");
      return (
        <div
          style={{
            backgroundColor: bg,
            color: "#000000",
            fontWeight: 600,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            width: "100%",
            height: "100%",
          }}
        >
          {params.value}
        </div>
      );
    },
  },
  {
    field: "fieldReportSentimentLabel",
    headerName: "Field Report Sentiment",
    flex: 1,
    minWidth: 60,
    align: "center",
    headerAlign: "center",
    renderCell: (params: any) => {
      const raw = (params.row.fieldReportSentimentColor ?? "").toLowerCase().trim();
      const SENTIMENT_COLORS: Record<string, string> = { green: "#73AA24", yellow: "#D0B232", red: "#D77440" };
      const bg = SENTIMENT_COLORS[raw] ?? (raw && raw !== "-" ? raw : "transparent");
      return (
        <div
          style={{
            backgroundColor: bg,
            color: "#000000",
            fontWeight: 600,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            width: "100%",
            height: "100%",
          }}
        >
          {params.value}
        </div>
      );
    },
  },
  { field: "forecastToGreenDate", headerName: "Forecast To Green Date", flex: 1.2, minWidth: 80, align: "center", headerAlign: "center" },
  {
    field: "accountTeamEngaged",
    headerName: "Account Team Engaged",
    flex: 0.7,
    minWidth: 40,
    align: "center",
    headerAlign: "center",
    renderCell: yesNoRenderCell,
    sortComparator: booleanLikeSortComparator,
  },
  {
    field: "scopingSentimentassessment",
    headerName: "Scoping/Sentiment Assessment",
    flex: 0.7,
    minWidth: 40,
    align: "center",
    headerAlign: "center",
    renderCell: yesNoRenderCell,
    sortComparator: booleanLikeSortComparator,
  },
  {
    field: "actionPlan",
    headerName: "Action Item",
    flex: 0.7,
    minWidth: 40,
    align: "center",
    headerAlign: "center",
    renderCell: yesNoRenderCell,
    sortComparator: booleanLikeSortComparator,
  },
  {
    field: "scoreCardCompletion",
    headerName: "ScoreCard Completion",
    flex: 0.7,
    minWidth: 40,
    align: "center",
    headerAlign: "center",
    renderCell: yesNoRenderCell,
    sortComparator: booleanLikeSortComparator,
  },
  { field: "cxECatalystPM", headerName: "CxE Catalyst PM", flex: 1.1, minWidth: 70, align: "center", headerAlign: "center" },
  { field: "mtpACRRange", headerName: "MTP ACR Range", flex: 1.0, minWidth: 80, align: "center", headerAlign: "center" },
  { field: "totalM365E5Seats", headerName: "Total M365 E5 Seats", flex: 1.1, minWidth: 70, align: "center", headerAlign: "center" },
  { field: "region", headerName: "Region", flex: 1.1, minWidth: 80, align: "center", headerAlign: "center" },
  { field: "tpid", headerName: "TPID", flex: 0.9, minWidth: 60, align: "center", headerAlign: "center" },
  { field: "tenantId", headerName: "TenantId", flex: 3.0, minWidth: 240, align: "center", headerAlign: "center" },
];

const renderCatalystHeaderWithTooltip = (headerName: string | undefined, tooltipText: string) => (
  <Tooltip title={tooltipText} arrow placement="top">
    <span
      style={{
        display: "inline-block",
        width: "100%",
        whiteSpace: "normal",
        lineHeight: 1.2,
        textAlign: "center",
        fontWeight: 700,
        cursor: "help",
        textDecoration: "underline dotted #A19F9D",
        textUnderlineOffset: 3,
        textDecorationThickness: 1,
      }}
    >
      {headerName}
    </span>
  </Tooltip>
);

const columns: GridColDef[] = baseColumns.map((column: GridColDef) => {
  const tooltip = CatalystTabTooltips.DataTableColumns[column.field];
  const renderHeader = tooltip
    ? (params: any) => renderCatalystHeaderWithTooltip(params.colDef.headerName, tooltip)
    : column.renderHeader;

  if (typeof column.flex !== "number") {
    return renderHeader ? { ...column, renderHeader } : column;
  }

  const widthFromRatio = Math.round(column.flex * COLUMN_WIDTH_UNIT);
  const minWidth = column.minWidth ?? 80;

  return {
    ...column,
    flex: undefined,
    width: Math.max(widthFromRatio, minWidth),
    renderHeader,
  };
});

export const CatalystDataTable: React.FC<CatalystDataTableProps> = ({ rows, isLoading = false }) => {
  return (
    <Stack tokens={{ childrenGap: 0 }} style={{ width: "100%" }}>
      <DataGrid
        rows={rows}
        columns={columns}
        loading={isLoading}
        autoHeight
        disableRowSelectionOnClick
        pageSizeOptions={[5, 10, 25]}
        initialState={{
          pagination: { paginationModel: { pageSize: 25 } },
        }}
        slotProps={{
          loadingOverlay: {
            variant: "skeleton" as any,
            noRowsVariant: "skeleton" as any,
          },
        }}
        rowHeight={36}
        columnHeaderHeight={68}
        sx={{
          border: "none",
          width: "100%",
          "& .MuiDataGrid-columnHeaders": {
            fontWeight: 700,
          },
          "& .MuiDataGrid-columnHeaderTitle": {
            fontWeight: 700,
            whiteSpace: "normal",
            lineHeight: 1.2,
            overflow: "visible",
            textOverflow: "clip",
            textAlign: "center",
          },
          "& .MuiDataGrid-columnHeaderTitleContainer": {
            whiteSpace: "normal",
            lineHeight: 1.2,
          },
          "& .MuiDataGrid-columnHeader": {
            justifyContent: "center",
          },
          // Hide the standalone sort icon entirely; sorting is reachable via the column menu (3-dot).
          // Keep the menu icon always visible at a fixed gutter so it never slides over the header text.
          "& .MuiDataGrid-iconButtonContainer": {
            display: "none",
          },
          "& .MuiDataGrid-menuIcon": {
            visibility: "visible",
            width: 18,
            marginRight: 0,
          },
          "& .MuiDataGrid-menuIcon .MuiIconButton-root": {
            padding: 2,
          },
          "& .MuiDataGrid-menuIconButton": {
            opacity: 0.55,
            fontSize: 16,
          },
          "& .MuiDataGrid-columnHeader:hover .MuiDataGrid-menuIconButton": {
            opacity: 1,
          },
          "& .MuiDataGrid-cell": {
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            textAlign: "center",
            padding: "0 4px",
          },
          "& .MuiDataGrid-cell[data-field='customerName']": {
            justifyContent: "flex-start",
            textAlign: "left",
            paddingLeft: "8px",
            "& a.catalyst-customer-link": {
              color: "#0078D4",
              textDecoration: "none",
              fontWeight: 600,
              display: "inline-flex",
              alignItems: "center",
              gap: 4,
              cursor: "pointer",
              "&:hover span": {
                textDecoration: "underline",
              },
              "&:hover": {
                color: "#004578",
              },
              "&:visited": {
                color: "#4C6EF5",
              },
            },
          },
          "& .MuiDataGrid-cell[data-field='mtpCompositeScore'], & .MuiDataGrid-cell[data-field='shiScore'], & .MuiDataGrid-cell[data-field='cxEReportedSentimentLabel'], & .MuiDataGrid-cell[data-field='fieldReportSentimentLabel']": {
            padding: 0,
            borderRight: "none",
          },
          "& .MuiDataGrid-cell[data-field='mtpCompositeScore'] > div, & .MuiDataGrid-cell[data-field='shiScore'] > div, & .MuiDataGrid-cell[data-field='cxEReportedSentimentLabel'] > div, & .MuiDataGrid-cell[data-field='fieldReportSentimentLabel'] > div": {
            width: "100%",
            height: "100%",
          },
          "& .MuiDataGrid-row:hover": {
            backgroundColor: "#F3F2F1",
          },
        }}
      />
    </Stack>
  );
};
